import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NewSessionDialog from '../Session/NewSessionDialog';
import StoryProgressSheet, { STORY_PROGRESS_SHEET_TITLE } from '../StoryProgress/StoryProgressSheet';
import { plottrackPath } from '../../utils/plottrackPath';
import { publishIdeaAction } from './ideaRoom';
import BookToolsGroup from '../BookTools/BookToolsGroup';
import {
  IDEA_MORE,
  IDEA_MORE_NEW,
  IDEA_MORE_SUMMARY,
  IDEA_MORE_VISIBLE,
  IDEA_NEEDS_BOOK,
  IDEA_ROOM_TITLE,
  TURN_INTO_SCENES,
  TURN_INTO_SCENES_HINT,
} from '../../utils/writerCopy';

/**
 * Idea's top bar. Turning the chat into scenes opens Outline's existing
 * suggestion offer. That offer is editable and stays off the Plot Grid
 * until the writer approves it.
 */
export default function IdeaRoomBar({
  storyId = null,
  projectId = null,
  storyName = '',
  sessionId = null,
  title = IDEA_ROOM_TITLE,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const ref = useRef(null);
  const progressBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const turnIntoScenes = () => {
    if (!storyId) return;
    navigate(plottrackPath(storyId), { state: { startGenerate: true } });
  };

  return (
    <header className="idea-bar">
      <h1 className="idea-bar__title">{title || IDEA_ROOM_TITLE}</h1>
      <BookToolsGroup />
      <div className="idea-bar__actions">
        <button
          type="button"
          className="idea-bar__btn idea-bar__progress"
          ref={progressBtnRef}
          aria-haspopup="dialog"
          aria-expanded={progressOpen}
          onClick={() => setProgressOpen(true)}
        >
          {STORY_PROGRESS_SHEET_TITLE}
        </button>
        <div className="idea-more" ref={ref}>
          <button
            type="button"
            className="idea-bar__btn"
            aria-label={IDEA_MORE}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {IDEA_MORE_VISIBLE}
          </button>
          {open && (
            <div className="idea-more__menu" role="menu" aria-label={IDEA_MORE}>
              <button
                type="button"
                role="menuitem"
                className="idea-more__item"
                disabled={!storyId}
                onClick={() => {
                  if (!storyId) return;
                  setOpen(false);
                  setShowNew(true);
                }}
              >
                {IDEA_MORE_NEW}
              </button>
              <button
                type="button"
                role="menuitem"
                className="idea-more__item"
                disabled={!sessionId}
                onClick={() => {
                  if (!sessionId) return;
                  setOpen(false);
                  publishIdeaAction('summary');
                }}
              >
                {IDEA_MORE_SUMMARY}
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          className="idea-bar__btn idea-bar__btn--primary"
          disabled={!storyId}
          title={storyId ? TURN_INTO_SCENES_HINT : IDEA_NEEDS_BOOK}
          onClick={turnIntoScenes}
        >
          {TURN_INTO_SCENES}
        </button>
      </div>
      <StoryProgressSheet
        open={progressOpen}
        onClose={() => {
          setProgressOpen(false);
          progressBtnRef.current?.focus();
        }}
        storyId={storyId}
      />
      {showNew && storyId && (
        <NewSessionDialog
          storyId={storyId}
          projectId={projectId}
          storyName={storyName}
          seriesAltitude={false}
          onClose={() => setShowNew(false)}
          onSessionCreated={(session) => {
            setShowNew(false);
            const nextId = session?.id || session?.data?.id;
            if (nextId) navigate(`/chat/${nextId}`);
          }}
        />
      )}
    </header>
  );
}
